import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Cloud, ShoppingBag, Rocket, Server, Banknote, HeartPulse, Music2, Globe2 } from "lucide-react";
import SectionLabel from "../components/SectionLabel";

const ITEMS = [
  { icon: Cloud, t: "SaaS & Technology", d: "Scaling technical success workflows and back-office engineering for cloud native platforms.", b: ["Reduced support backlog by 40%", "24/7 infrastructure scale", "Faster user onboarding"] },
  { icon: ShoppingBag, t: "E-commerce & D2C", d: "Optimizing global supply chain management and last-mile delivery through integrated workflows.", b: ["Improved SLA fulfillment", "Integrated supply chain sync", "Lower operational overhead"] },
  { icon: Rocket, t: "Startups & SMBs", d: "Providing agile, tech-integrated delivery systems designed for rapid growth and efficiency.", b: ["Faster speed-to-market", "Optimized burn rate", "Scalable burst capacity"] },
  { icon: Server, t: "IT Services", d: "Elevating service delivery maturity and technical excellence through managed delivery systems.", b: ["Standardized quality rigor", "Higher project profitability", "Global team scale"] },
  { icon: Banknote, t: "FinTech", d: "Securing high-stakes financial operations and transaction pipelines with rigorous security protocols.", b: ["Guaranteed compliance", "Secure transaction throughput", "Automated audit trails"] },
  { icon: HeartPulse, t: "Healthcare Support", d: "Delivering compliant data processing and specialized technical ops for diagnostic healthcare systems.", b: ["HIPAA-compliant integrity", "Accelerated diagnostic reporting", "Patient success workflows"] },
  { icon: Music2, t: "Events & Entertainment", d: "Orchestrating real-time logistical coordination and digital engagement at global scale.", b: ["Zero-downtime reliability", "Real-time logistics sync", "Scalable attendee support"] },
  { icon: Globe2, t: "Global Enterprises", d: "Optimizing legacy operations through strategic shared services and intelligent automation systems.", b: ["Enterprise RPA efficiency", "Massive cost savings", "Strategic scale consulting"] },
];

const CASES = [
  { tag: "FINTECH", t: "Back-Office Automation", d: "Modernized critical financial processing for a global institution, resulting in a 65% increase in operational throughput and significant reduction in manual error overhead." },
  { tag: "CLOUD TECH", t: "SaaS Infrastructure Scale", d: "Engineered a multi-cloud scalability framework for a tier-1 SaaS partner. Achieved 99.9% SLA reliability while optimizing cloud storage costs by 30% through automated tiering." },
  { tag: "E-COMMERCE", t: "Supply Chain Digitization", d: "Streamlined last-mile logistics for a D2C leader. Cut processing lead times by 25% and improved inventory accuracy to 99.8% using integrated AI forecasting tools." },
];

export default function Industries() {
  return (
    <div data-testid="industries-page" className="bg-[#050505] text-white">
      <section className="pt-32 pb-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Industries We Serve</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter mt-6 leading-[1.05] max-w-4xl">
            Operations Redefined for <span className="text-[#3B82F6]">Global Scale.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl">
            Operexa Gennex serves as a specialized operations and technology partner for global enterprises, delivering outcomes that prioritize efficiency, mission-critical reliability, and rapid organizational scale.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Target Industries Highlight</SectionLabel>
          <h2 className="font-display text-2xl lg:text-4xl font-semibold tracking-tight mt-4 max-w-3xl">
            Driving high-impact outcomes across high-growth global sectors.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {ITEMS.map((it, i) => {
              const Ic = it.icon;
              return (
                <motion.div
                  key={it.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 4) * 0.05 }}
                  data-testid={`detail-industry-${i}`}
                  className="bg-[#050505] p-7 hover:bg-[#0a0a0c] transition-colors"
                >
                  <Ic className="w-6 h-6 text-[#3B82F6] mb-4" />
                  <h3 className="font-display text-lg font-semibold mb-2">{it.t}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-4">{it.d}</p>
                  <ul className="space-y-1.5 text-xs text-zinc-500">
                    {it.b.map((p) => <li key={p} className="flex gap-2"><span className="w-1 h-1 mt-1.5 bg-[#3B82F6] block" />{p}</li>)}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel>Strategic Outcomes</SectionLabel>
          <h2 className="font-display text-2xl lg:text-4xl font-semibold tracking-tight mt-4 max-w-3xl">
            Industry Case Studies.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CASES.map((c) => (
              <div key={c.t} className="border border-white/10 bg-[#0a0a0c] p-8">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-3">{c.tag}</div>
                <h3 className="font-display text-xl font-semibold mb-3">{c.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight max-w-3xl mx-auto">
            Discuss your industry use case with Operexa Gennex.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
            Consult with our technical architects to engineer your industry-specific operational success and scale.
          </p>
          <Link to="/contact" data-testid="industries-bottom-cta" className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-[#3B82F6] hover:bg-[#60A5FA] font-semibold">
            Strategy Consultation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
